"use client";

import Link, { LinkProps } from "next/link";
import React from "react";
import { useRouter } from "next/navigation";
import { useShutter } from "@/components/providers/ShutterController";

interface TransitionLinkProps extends LinkProps {
  children: React.ReactNode;
  href: string;
  className?: string;
  style?: React.CSSProperties;
  /**
   * @deprecated Previously rendered as the per-link transition heading.
   * The shared shutter mark replaced that overlay, so this prop is now
   * ignored. Kept on the props to avoid a sweep through the call sites.
   */
  title?: string;
  "aria-label"?: string;
  onMouseLeave?: () => void;
}

/**
 * Internal-link wrapper that drives the global {@link ShutterController}
 * on click instead of owning its own overlay.
 *
 * Why this matters:
 *   The previous implementation rendered an `<AnimatePresence>` exit
 *   animation inside the source page. When `router.push` unmounted the
 *   source subtree the animation never got to play, so on fast routes
 *   the shutter would visibly pop out instead of sliding back up. The
 *   global controller lives above the route boundary, so its phase
 *   state and timers survive the navigation and the close → covered →
 *   opening cycle always plays in full.
 */
export const TransitionLink: React.FC<TransitionLinkProps> = ({
  children,
  href,
  className,
  style,
  title,
  ...props
}) => {
  void title;
  const router = useRouter();
  const { runShutter } = useShutter();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Honour modifier-clicks (open in new tab / new window / save as).
    // Only takeover plain left-clicks.
    if (
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      e.button !== 0
    ) {
      return;
    }
    e.preventDefault();
    runShutter(() => router.push(href));
  };

  return (
    <Link
      {...props}
      href={href}
      className={`relative z-10 ${className ?? ""}`}
      onClick={handleClick}
      style={style}
    >
      {children}
    </Link>
  );
};
